/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global document, Office, Word */

Office.onReady((info) => {
  if (info.host === Office.HostType.Word) {
    const sideload = document.getElementById("sideload-msg");
    const appBody = document.getElementById("app-body");
    if (sideload) sideload.style.display = "none";
    if (appBody) appBody.style.display = "flex";
    initializeUiEventHandlers();
    restoreSavedConfigToInputs();
    setupSignaturePad();
  }
});

// --- UI wiring and helpers ---

function initializeUiEventHandlers(): void {
  addClickHandler("save-config", handleSaveConfig);
  addClickHandler("load-participants", handleLoadParticipants);
  addClickHandler("insert-field", handleInsertSelectedField);
  addClickHandler("insert-cc", handleInsertContentControl);
  addClickHandler("sig-clear", clearSignaturePad);
  addClickHandler("sig-insert", handleInsertSignatureFromCanvas);
  addClickHandler("stamp-insert", handleInsertStampFromFile);
  addClickHandler("save-template", handleSaveTemplateToOneHr);
  addClickHandler("sign-insert", handleSignInsertPlaceholder);
  addClickHandler("generate-pdf", handleGeneratePdfPlaceholder);
  addClickHandler("mark-signed", handleMarkSignedPlaceholder);
  addClickHandler("load-assets", handleLoadAssetsFromOneHr);

  // Delegate clicks for any element carrying data-insert-key="..."
  document.addEventListener("click", async (event) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    const trigger = target.closest("[data-insert-key]") as HTMLElement | null;
    if (!trigger) return;
    const key = trigger.getAttribute("data-insert-key") || "";
    const label = trigger.getAttribute("data-insert-label") || "";
    if (!key) return;
    event.preventDefault();
    await insertFieldWithKeyOnly(key, label);
  });
}

function addClickHandler(elementId: string, handler: (ev: MouseEvent) => void): void {
  const el = document.getElementById(elementId);
  if (el) {
    el.addEventListener("click", handler);
  }
}

function setStatus(message: string): void {
  const status = document.getElementById("status");
  if (status) status.textContent = message;
}

// --- Config storage ---

function restoreSavedConfigToInputs(): void {
  const baseUrl = localStorage.getItem("onehr.baseUrl") || "";
  const apiKey = localStorage.getItem("onehr.apiKey") || "";
  const templateId = localStorage.getItem("onehr.templateId") || "";
  const contractId = localStorage.getItem("onehr.contractId") || "";
  setInputValue("onehr-base-url", baseUrl);
  setInputValue("onehr-api-key", apiKey);
  setInputValue("onehr-template-id", templateId);
  setInputValue("contract-id", contractId);
}

function setInputValue(elementId: string, value: string): void {
  const input = document.getElementById(elementId) as HTMLInputElement | null;
  if (input) input.value = value;
}

function getInputValue(elementId: string): string {
  const input = document.getElementById(elementId) as HTMLInputElement | null;
  return input?.value?.trim() || "";
}

function handleSaveConfig(): void {
  localStorage.setItem("onehr.baseUrl", getInputValue("onehr-base-url"));
  localStorage.setItem("onehr.apiKey", getInputValue("onehr-api-key"));
  localStorage.setItem("onehr.templateId", getInputValue("onehr-template-id"));
  const contractId = getInputValue("contract-id");
  if (contractId) localStorage.setItem("onehr.contractId", contractId);
  setStatus("Config saved.");
}

// --- Participants ---

async function handleLoadParticipants(): Promise<void> {
  try {
    const baseUrl = getInputValue("onehr-base-url");
    const apiKey = getInputValue("onehr-api-key");
    if (!baseUrl || !apiKey) {
      setStatus("Enter Base URL and API Key first.");
      return;
    }
    setStatus("Loading participants...");
    const response = await fetch(`${baseUrl.replace(/\/$/, "")}/participants`, {
      headers: { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" },
      credentials: "include",
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const participants: Array<Record<string, unknown>> = await response.json();
    populateParticipantsDropdown(participants);
    setStatus(`Loaded ${participants.length} participant(s).`);
  } catch (error) {
    setStatus(
      `Failed to load participants. You can still test field insertion using manual data. (${(error as Error).message})`
    );
    // Populate with sample data to enable testing
    populateParticipantsDropdown([
      {
        id: "p1",
        firstName: "Alex",
        lastName: "Kim",
        email: "alex@example.com",
        phone: "+1-555-0100",
        role: "signer",
      },
      {
        id: "p2",
        firstName: "Jamie",
        lastName: "Lee",
        email: "jamie@example.com",
        phone: "+1-555-0101",
        role: "signer",
      },
    ]);
  }
}

function populateParticipantsDropdown(items: Array<Record<string, unknown>>): void {
  const select = document.getElementById("participants-list") as HTMLSelectElement | null;
  if (!select) return;
  select.innerHTML = "";
  for (const item of items) {
    const option = document.createElement("option");
    const firstName = String(item["firstName"] ?? "");
    const lastName = String(item["lastName"] ?? "");
    const email = String(item["email"] ?? "");
    option.value = String(item["id"] ?? email ?? firstName);
    option.textContent = `${firstName} ${lastName} ${email ? `(${email})` : ""}`.trim();
    option.dataset.item = JSON.stringify(item);
    select.appendChild(option);
  }
}

function getSelectedParticipant(): Record<string, unknown> | null {
  const select = document.getElementById("participants-list") as HTMLSelectElement | null;
  if (!select || !select.selectedOptions.length) return null;
  const data = select.selectedOptions[0].dataset.item;
  return data ? (JSON.parse(data) as Record<string, unknown>) : null;
}

// --- Word actions ---

async function handleInsertSelectedField(): Promise<void> {
  // Cache DOM elements once
  const participantField = document.getElementById("participant-field") as HTMLSelectElement | null;
  const participant = getSelectedParticipant();
  const field = participantField?.value || "";
  const customKey = getInputValue("custom-field-key");

  if (!participant || !field) {
    setStatus("Choose a participant and field.");
    return;
  }

  const key = customKey || field;

  // Show loading state
  setStatus(`Inserting field "${key}"...`);

  try {
    // Batch all Word operations in a single run
    await Word.run(async (context) => {
      const range = context.document.getSelection();

      // Load the text property before deciding how to insert
      context.load(range, "text");
      await context.sync();

      const placeholder = `{{${key}}}`;
      const marker = "\u200B"; // zero-width space to create a definite range

      let target: Word.Range;
      if (range.text && range.text.trim().length > 0) {
        // Replace current selection with marker; we'll wrap that marker
        target = range.insertText(marker, Word.InsertLocation.replace);
      } else {
        // No selection: insert marker at caret position
        target = range.insertText(marker, Word.InsertLocation.replace);
      }

      const cc = target.insertContentControl();

      // Set properties
      cc.tag = `onehr:${key}`;
      cc.title = `OneHR • ${key}`;
      cc.appearance = "BoundingBox";

      // Replace marker inside the CC with the placeholder
      cc.insertText(placeholder, Word.InsertLocation.replace);

      // Colorize the placeholder with black color
      const textRange = cc.getRange();
      textRange.font.color = "#000000";

      await context.sync();
    });

    // Show success state briefly
    setStatus(`Field "${key}" inserted successfully!`);
    showToast(`Field "${key}" added to document`, "success");

    // Notify UI update after Word operations complete
    notifyUsageCountsChanged();
    notifyInsertComplete();

    // Clear success message after 2 seconds
    setTimeout(() => {
      setStatus("Ready to insert more fields.");
    }, 2000);
  } catch (error) {
    // Show error state
    const errorMsg = `Failed to insert field "${key}": ${(error as Error).message}`;
    setStatus(errorMsg);
    showToast(errorMsg, "error");
  }
}

async function handleInsertContentControl(): Promise<void> {
  await Word.run(async (context) => {
    const range = context.document.getSelection();
    const cc = range.insertContentControl();
    cc.tag = "onehr-field";
    cc.title = "OneHR Field";
    cc.appearance = "BoundingBox";
    await context.sync();
  });
}

async function insertFieldWithKeyOnly(key: string, label?: string): Promise<void> {
  if (!key) return;

  // If this looks like an image/signature field, insert a visual box
  if (isImageKey(key)) {
    setStatus(`Inserting image placeholder for "${key}"...`);
    try {
      await insertSignaturePlaceholderBox(key, label);
      setStatus(`Image placeholder "${key}" inserted successfully!`);
      showToast(`Image placeholder "${key}" added to document`, "success");
      notifyUsageCountsChanged();
      notifyInsertComplete();

      // Clear success message after 2 seconds
      setTimeout(() => {
        setStatus("Ready to insert more fields.");
      }, 2000);
    } catch (error) {
      const errorMsg = `Failed to insert image placeholder "${key}": ${(error as Error).message}`;
      setStatus(errorMsg);
      showToast(errorMsg, "error");
    }
    return;
  }

  // Show loading state for text fields
  setStatus(`Inserting field "${key}"...`);

  try {
    // Batch all Word operations in a single run
    await Word.run(async (context) => {
      const range = context.document.getSelection();

      // Load the text property before deciding how to insert
      context.load(range, "text");
      await context.sync();

      const friendly = label && label.trim().length > 0 ? label.trim() : key;
      const placeholder = `{{${key}}}`;
      const marker = "\u200B";

      let target: Word.Range;
      if (range.text && range.text.trim().length > 0) {
        target = range.insertText(marker, Word.InsertLocation.replace);
      } else {
        target = range.insertText(marker, Word.InsertLocation.replace);
      }

      const cc = target.insertContentControl();

      // Set properties
      cc.tag = `onehr:${key}`;
      cc.title = `OneHR • ${friendly}`;
      cc.appearance = "BoundingBox";

      // Replace marker with placeholder
      cc.insertText(placeholder, Word.InsertLocation.replace);

      // Colorize the placeholder with black color
      const textRange = cc.getRange();
      textRange.font.color = "#000000";

      await context.sync();
    });

    // Show success state briefly
    setStatus(`Field "${key}" inserted successfully!`);
    showToast(`Field "${key}" added to document`, "success");

    // Notify UI update after Word operations complete
    notifyUsageCountsChanged();
    notifyInsertComplete();

    // Clear success message after 2 seconds
    setTimeout(() => {
      setStatus("Ready to insert more fields.");
    }, 2000);
  } catch (error) {
    // Show error state
    const errorMsg = `Failed to insert field "${key}": ${(error as Error).message}`;
    setStatus(errorMsg);
    showToast(errorMsg, "error");
  }
}

function notifyUsageCountsChanged(): void {
  try {
    window.dispatchEvent(new CustomEvent("onehr:usage-updated"));
  } catch {}
}

function notifyInsertComplete(): void {
  try {
    window.dispatchEvent(new CustomEvent("onehr:insert-complete"));
  } catch {}
}

function isImageKey(key: string): boolean {
  const k = key.toLowerCase();
  return k.endsWith("_image") || k.includes("signature");
}

async function getUsageCountsForKeys(keys: string[]): Promise<Record<string, number>> {
  const initial: Record<string, number> = {};
  for (const k of keys) initial[k] = 0;
  const counts = await Word.run(async (context) => {
    const map: Record<string, number> = { ...initial };

    // Count content controls with tags
    const ccs = context.document.contentControls;
    context.load(ccs, ["tag", "text"]);
    await context.sync();

    for (let i = 0; i < ccs.items.length; i++) {
      const cc = ccs.items[i];
      const tag = cc.tag || "";
      const text = cc.text || "";

      // Count by tag if it has onehr: prefix
      if (tag.startsWith("onehr:")) {
        const key = tag.substring(6);
        // Extract base key without pipe format (e.g., "key|format" -> "key")
        const baseKey = key.split("|")[0].trim();
        if (map[baseKey] !== undefined) map[baseKey] += 1;
      }

      // Also check text for placeholders (in case content control has no tag)
      if (text && text.includes("{{") && text.includes("}}")) {
        const matches = text.match(/\{\{([^}|]+)(\|[^}]+)?\}\}/g);
        if (matches) {
          matches.forEach((match) => {
            // Extract key from {{key}} or {{key|format}}
            const extracted = match.replace(/^\{\{|\}\}$/g, "");
            const baseKey = extracted.split("|")[0].trim();
            if (map[baseKey] !== undefined) {
              // Only count if not already counted by tag
              if (!tag.startsWith("onehr:")) {
                map[baseKey] += 1;
              }
            }
          });
        }
      }
    }

    // Also search document body for plain text placeholders (not in content controls)
    const body = context.document.body;
    context.load(body, "text");
    await context.sync();

    if (body.text && body.text.includes("{{") && body.text.includes("}}")) {
      // Count all {{placeholder}} or {{placeholder|format}} patterns
      const bodyMatches = body.text.match(/\{\{([^}|]+)(\|[^}]+)?\}\}/g);
      if (bodyMatches) {
        // Create a set to track already counted placeholders from content controls
        const alreadyCounted: Record<string, number> = {};

        bodyMatches.forEach((match) => {
          // Extract key from {{key}} or {{key|format}}
          const extracted = match.replace(/^\{\{|\}\}$/g, "");
          const baseKey = extracted.split("|")[0].trim();

          if (map[baseKey] !== undefined) {
            alreadyCounted[baseKey] = (alreadyCounted[baseKey] || 0) + 1;
          }
        });

        // Update map with body counts (body.text includes content control text, so this gives total)
        Object.keys(alreadyCounted).forEach((key) => {
          map[key] = alreadyCounted[key];
        });
      }
    }

    return map;
  });
  return counts;
}

// Expose to React UI
(window as unknown as Record<string, unknown>)["onehrGetUsageCountsForKeys"] =
  getUsageCountsForKeys;
(window as unknown as Record<string, unknown>)["onehrInsertPdfFormField"] =
  handleInsertPdfFormField;
(window as unknown as Record<string, unknown>)["onehrInsertPdfFormFieldDirect"] =
  handleInsertPdfFormFieldDirect;

// --- Signature pad ---

let signaturePadCanvas: HTMLCanvasElement | null = null;
let isDrawing = false;

function setupSignaturePad(): void {
  signaturePadCanvas = document.getElementById("signature-pad") as HTMLCanvasElement | null;
  if (!signaturePadCanvas) return;
  const ctx = signaturePadCanvas.getContext("2d");
  if (!ctx) return;
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 2;
  const start = (x: number, y: number) => {
    isDrawing = true;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (x: number, y: number) => {
    if (!isDrawing) return;
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const stop = () => {
    isDrawing = false;
  };
  const pos = (evt: MouseEvent | TouchEvent) => {
    const rect = signaturePadCanvas!.getBoundingClientRect();
    const clientX = (evt as TouchEvent).touches?.[0]?.clientX ?? (evt as MouseEvent).clientX;
    const clientY = (evt as TouchEvent).touches?.[0]?.clientY ?? (evt as MouseEvent).clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };
  signaturePadCanvas.addEventListener("mousedown", (e) => start(pos(e).x, pos(e).y));
  signaturePadCanvas.addEventListener("mousemove", (e) => move(pos(e).x, pos(e).y));
  window.addEventListener("mouseup", stop);
  signaturePadCanvas.addEventListener("touchstart", (e) => {
    const p = pos(e);
    start(p.x, p.y);
  });
  signaturePadCanvas.addEventListener("touchmove", (e) => {
    const p = pos(e);
    move(p.x, p.y);
  });
  window.addEventListener("touchend", stop);
}

function clearSignaturePad(): void {
  if (!signaturePadCanvas) return;
  const ctx = signaturePadCanvas.getContext("2d");
  if (!ctx) return;
  ctx.clearRect(0, 0, signaturePadCanvas.width, signaturePadCanvas.height);
}

async function handleInsertSignatureFromCanvas(): Promise<void> {
  if (!signaturePadCanvas) return;
  const dataUrl = signaturePadCanvas.toDataURL("image/png");
  const base64 = dataUrl.split(",")[1];
  await insertBase64PngAtSelection(base64);
}

async function handleInsertStampFromFile(): Promise<void> {
  const input = document.getElementById("stamp-file") as HTMLInputElement | null;
  if (!input || !input.files || input.files.length === 0) {
    setStatus("Choose an image file for the stamp.");
    return;
  }
  const file = input.files[0];
  const base64 = await fileToBase64(file);
  const base64Data = base64.split(",")[1];
  await insertBase64PngAtSelection(base64Data);
}

async function insertBase64PngAtSelection(base64Png: string): Promise<void> {
  await Word.run(async (context) => {
    const range = context.document.getSelection();
    range.insertInlinePictureFromBase64(base64Png, Word.InsertLocation.replace);
    await context.sync();
  });
}

async function insertSignaturePlaceholderBox(key: string, label?: string): Promise<void> {
  const friendly = label && label.trim().length > 0 ? label.trim() : key;
  const dataUrl = generatePlaceholderPngDataUrl(friendly);
  const base64 = dataUrl.split(",")[1];

  // Batch all Word operations in a single run with minimal sync calls
  await Word.run(async (context) => {
    const selection = context.document.getSelection();
    const paras = selection.paragraphs;
    context.load(paras, "items");

    // First sync to get paragraph info
    await context.sync();

    const firstPara = paras.items.length ? paras.items[0] : null;
    const startRange = firstPara ? firstPara.getRange("Start") : selection;

    // Insert the image BEFORE the start of the current paragraph so it appears above the underline
    const pic = startRange.insertInlinePictureFromBase64(base64, Word.InsertLocation.before);
    pic.select();

    // Get the new selection and insert content control
    const sel = context.document.getSelection();
    const cc = sel.insertContentControl();

    // Set all properties at once
    cc.tag = `onehr:${key}`;
    cc.title = `OneHR • ${friendly}`;
    cc.appearance = "BoundingBox";

    // Final sync for all operations
    await context.sync();
  });
}

// Cache canvas and context for better performance
let placeholderCanvas: HTMLCanvasElement | null = null;
let placeholderContext: CanvasRenderingContext2D | null = null;

function generatePlaceholderPngDataUrl(text: string, width = 220, height = 90): string {
  // Reuse canvas if dimensions match, otherwise create new one
  if (
    !placeholderCanvas ||
    placeholderCanvas.width !== width ||
    placeholderCanvas.height !== height
  ) {
    placeholderCanvas = document.createElement("canvas");
    placeholderCanvas.width = width;
    placeholderCanvas.height = height;
    placeholderContext = placeholderCanvas.getContext("2d");
  }

  if (!placeholderContext) return "";

  // Clear canvas for reuse
  placeholderContext.clearRect(0, 0, width, height);

  // Background
  placeholderContext.fillStyle = "#ffffff";
  placeholderContext.fillRect(0, 0, width, height);

  // Border (dashed)
  placeholderContext.strokeStyle = "#9ca3af";
  placeholderContext.lineWidth = 2;
  placeholderContext.setLineDash([6, 6]);
  placeholderContext.strokeRect(3, 3, width - 6, height - 6);

  // Text {{key}}
  placeholderContext.setLineDash([]);
  placeholderContext.fillStyle = "#6b7280";
  placeholderContext.font = "13px sans-serif";
  const label = `{{${text}}}`;
  const textWidth = placeholderContext.measureText(label).width;
  placeholderContext.fillText(label, Math.max(8, (width - textWidth) / 2), height / 2 + 5);

  return placeholderCanvas.toDataURL("image/png");
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

// --- Save/Sign placeholders ---

async function handleSaveTemplateToOneHr(): Promise<void> {
  try {
    setStatus("Collecting document content...");
    const { baseUrl, apiKey, templateId, contractId } = getConfig();
    if (!baseUrl || !apiKey) {
      setStatus("Enter Base URL and API Key first.");
      return;
    }

    // Collect content controls for template metadata
    const contentControls = await collectContentControls();
    const payload = { contentControls };

    // Attach optional templateId for server-side linkage
    (payload as unknown as Record<string, unknown>)["templateId"] = templateId || undefined;
    setStatus("Uploading to OneHR...");
    const resp = await fetch(
      `${baseUrl.replace(/\/$/, "")}/contracts${contractId ? `/${encodeURIComponent(contractId)}` : ""}`,
      {
        method: contractId ? "PUT" : "POST",
        headers: { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      }
    );
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = (await resp.json()) as { id?: string };
    if (data?.id) {
      localStorage.setItem("onehr.contractId", data.id);
      setInputValue("contract-id", data.id);
    }
    setStatus("Saved to OneHR.");
    showToast("Contract saved to OneHR", "success");
  } catch (error) {
    setStatus(`Save failed: ${(error as Error).message}`);
    showToast(`Save failed: ${(error as Error).message}`, "error");
  }
}

type OneHrUploadPayload = {
  contentControls: Array<{ tag: string; title: string; text: string }>;
};

function getConfig(): { baseUrl: string; apiKey: string; templateId: string; contractId: string } {
  return {
    baseUrl: getInputValue("onehr-base-url"),
    apiKey: getInputValue("onehr-api-key"),
    templateId: getInputValue("onehr-template-id"),
    contractId: getInputValue("contract-id"),
  };
}

async function collectContentControls(): Promise<
  Array<{ tag: string; title: string; text: string }>
> {
  return Word.run(async (context) => {
    const ccs = context.document.contentControls;
    context.load(ccs, ["tag", "title", "text"]);
    await context.sync();
    const result: Array<{ tag: string; title: string; text: string }> = [];
    for (let i = 0; i < ccs.items.length; i++) {
      const cc = ccs.items[i];
      result.push({ tag: cc.tag || "", title: cc.title || "", text: cc.text || "" });
    }
    return result;
  });
}

async function handleLoadAssetsFromOneHr(): Promise<void> {
  try {
    const { baseUrl, apiKey } = getConfig();
    if (!baseUrl || !apiKey) {
      setStatus("Enter Base URL and API Key first.");
      return;
    }
    setStatus("Loading assets...");
    const resp = await fetch(`${baseUrl.replace(/\/$/, "")}/assets`, {
      headers: { Authorization: `Bearer ${apiKey}`, "Content-Type": "application/json" },
      credentials: "include",
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const assets = (await resp.json()) as Array<{
      id: string;
      name: string;
      type: string;
      base64?: string;
      url?: string;
    }>; // type: signature|stamp
    renderAssets(assets);
    setStatus(`Loaded ${assets.length} asset(s).`);
  } catch (error) {
    setStatus(`Failed to load assets: ${(error as Error).message}`);
  }
}

function renderAssets(
  assets: Array<{ id: string; name: string; type: string; base64?: string; url?: string }>
): void {
  const container = document.getElementById("assets-container");
  if (!container) return;
  container.innerHTML = "";
  for (const a of assets) {
    const row = document.createElement("div");
    row.style.display = "flex";
    row.style.alignItems = "center";
    row.style.columnGap = "8px";
    const img = document.createElement("img");
    img.alt = a.name;
    img.style.maxHeight = "40px";
    img.style.maxWidth = "120px";
    if (a.base64) img.src = `data:image/png;base64,${a.base64}`;
    else if (a.url) img.src = a.url;
    const label = document.createElement("span");
    label.textContent = `${a.type}: ${a.name}`;
    const btn = document.createElement("button");
    btn.className = "ms-Button";
    btn.innerHTML = '<span class="ms-Button-label">Insert</span>';
    btn.addEventListener("click", async () => {
      if (a.base64) {
        await insertBase64PngAtSelection(a.base64);
      } else if (a.url) {
        try {
          const res = await fetch(a.url);
          const blob = await res.blob();
          const reader = new FileReader();
          reader.onload = async () => {
            const dataUrl = String(reader.result);
            const base64 = dataUrl.split(",")[1];
            await insertBase64PngAtSelection(base64);
          };
          reader.readAsDataURL(blob);
        } catch {}
      }
    });
    row.appendChild(img);
    row.appendChild(label);
    row.appendChild(btn);
    container.appendChild(row);
  }
}

/**
 * Insert a PDF form field with underline for filling
 * @param labelText - The label text for the form field
 * @param key - Optional key for content control tagging
 */
async function insertPdfFormField(
  labelText: string,
  key?: string,
  category?: string
): Promise<void> {
  await Word.run(async (context) => {
    const selection = context.document.getSelection();

    // 1) Insert "Label: " text
    const labelRange = selection.insertText("", Word.InsertLocation.replace);

    // 2) Right after label, insert an underline "____________________"
    const fieldRange = labelRange.insertText("____________________", Word.InsertLocation.after);

    // 3) Mark this range with a content control for internal use
    const cc = fieldRange.insertContentControl();
    const fieldKey = key || `FormField_${labelText}`;
    cc.title = `${labelText}`;
    cc.tag = `onehr:pdf:${fieldKey}`;
    cc.appearance = "BoundingBox";
    cc.color = "#3B82F6"; // Blue color

    await context.sync();
  });
}

/**
 * Insert PDF form field with label and key (exposed to window for direct UI calls)
 */
async function handleInsertPdfFormFieldDirect(
  label: string,
  key?: string,
  category?: string
): Promise<void> {
  if (!label || !label.trim()) {
    setStatus("Please provide a label for the PDF form field");
    return;
  }

  try {
    setStatus(`Inserting PDF form field: ${label}...`);
    await insertPdfFormField(label, key, category);
    setStatus(`PDF form field "${label}" inserted successfully!`);
    showToast(`PDF form field "${label}" added`, "success");
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    setStatus(`Error inserting PDF form field: ${errorMessage}`);
    showToast(`Error: ${errorMessage}`, "error");
  }
}

/**
 * Insert PDF form field by key (exposed to window for UI calls with manual inputs)
 */
async function handleInsertPdfFormField(): Promise<void> {
  const labelInput = document.getElementById("pdf-field-label") as HTMLInputElement;
  const keyInput = document.getElementById("pdf-field-key") as HTMLInputElement;

  if (!labelInput || !labelInput.value) {
    setStatus("Please enter a label for the PDF form field");
    return;
  }

  const label = labelInput.value.trim();
  const key = keyInput?.value?.trim() || undefined;

  try {
    setStatus(`Inserting PDF form field: ${label}...`);
    await insertPdfFormField(label, key);
    setStatus(`PDF form field "${label}" inserted successfully!`);
    showToast(`PDF form field "${label}" added`, "success");

    // Clear inputs
    labelInput.value = "";
    if (keyInput) keyInput.value = "";

    notifyUsageCountsChanged();
    notifyInsertComplete();
  } catch (error) {
    const errorMsg = `Failed to insert PDF form field: ${(error as Error).message}`;
    setStatus(errorMsg);
    showToast(errorMsg, "error");
  }
}

function handleSignInsertPlaceholder(): void {
  setStatus("Sign action triggered (stub). Use Insert Signature to place image.");
}

function handleGeneratePdfPlaceholder(): void {
  setStatus(
    "PDF generation is not available in Word on the web via Office.js. Export on server side after upload."
  );
}

function handleMarkSignedPlaceholder(): void {
  setStatus("Marked as signed (stub).");
}

function showToast(message: string, kind: "success" | "error" | "info" = "info"): void {
  const toast = document.createElement("div");
  toast.textContent = message;
  toast.style.position = "fixed";
  toast.style.right = "16px";
  toast.style.bottom = "16px";
  toast.style.zIndex = "99999";
  toast.style.padding = "10px 14px";
  toast.style.borderRadius = "6px";
  toast.style.color = "#fff";
  toast.style.fontSize = "12px";
  toast.style.boxShadow = "0 2px 8px rgba(0,0,0,0.25)";
  toast.style.background =
    kind === "success" ? "#16a34a" : kind === "error" ? "#dc2626" : "#334155";
  document.body.appendChild(toast);
  setTimeout(() => {
    toast.style.opacity = "0";
    toast.style.transition = "opacity 200ms ease";
    setTimeout(() => toast.remove(), 220);
  }, 2500);
}
